/**
 * Storlyn 品牌支付配置
 */
import type { BrandPaymentConfig } from '../types'
import { chapterCommonTexts, payCommonTexts, subscribeCommonTexts } from '../common'
import { colors as brandColors, theme as brandTheme } from '@/config/brands'

function hexToRgba(color: string, alpha: number) {
  if (!color.startsWith('#'))
    return color

  const hex = color.slice(1)
  const normalizedHex = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16)
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16)
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const primaryGradientStart = '#FF81B0'
const primaryGradientEnd = '#EE3BA4'
const primaryGradient = `linear-gradient(90deg, ${primaryGradientStart} 0%, ${primaryGradientEnd} 100%)`
const paymentStickyBgStart = brandTheme.bgGlobal || '#FFFCFE'
const paymentStickyGradient = `linear-gradient(to top, ${paymentStickyBgStart} 0%, ${paymentStickyBgStart} 34%, ${hexToRgba(paymentStickyBgStart, 0.92)} 58%, ${hexToRgba(paymentStickyBgStart, 0.72)} 74%, ${hexToRgba(paymentStickyBgStart, 0.36)} 88%, ${hexToRgba(paymentStickyBgStart, 0)} 100%)`

export const storlynPaymentConfig: BrandPaymentConfig = {
  brand: 'storlyn',

  pay: {
    theme: {
      isLightPaymentSelector: true,
      paymentStickyGradient,
      colors: {
        pageBg: '#fdf2f8',
        cardBg: '#fff',
        cardBorder: '#e5e7eb',
        cardActiveBorder: '#ec3a9c',
        cardActiveShadow: 'rgba(236, 58, 156, 0.16)',
        cardRightBg: '#fdf2f8',
        bonusPillBg: '#fde7f2',
        bonusPillText: '#ec3a9c',
        bonusRateText: '#ec3a9c',
        bottomBarBg: 'rgba(255, 255, 255, 0.98)',
        bottomBarBorder: '#e5e7eb',
        totalHighlight: '#ec3a9c',
        payBtnGradient: 'linear-gradient(90deg, #FF81B0 0%, #EE3BA4 100%)',
        payBtnShadow: 'rgba(236, 58, 156, 0.22)',
        tagGradient: 'linear-gradient(90deg, #FF4754 0%, #FEA94C 100%)',
      },
    },
    texts: payCommonTexts,
    resources: {
      goldCoinIcon: '/images/storlyn/welfare-gold.png',
    },
  },

  subscribe: {
    texts: subscribeCommonTexts,
    darkPaymentSelector: false,
    resources: {
      vipBg: '/images/storlyn/vip_bg.webp',
      memberSubscribeBg: '/images/storlyn/member_subscribe_bg.webp',
      vipIcon: '/images/storlyn/vip_icon2.png',
      vipDiscountIcon: '/images/storlyn/vip_discount.png',
      discountBg: '/images/storlyn/discount.avif',
      showVipIcon: true,
    },
    colors: {
      redGradient: primaryGradient,
      purpleTextGradient: 'linear-gradient(180deg, #FF7FB5 0%, #EE3BA4 100%)',
      subscribeText1: brandColors!.primary,
      subscribeText2: brandColors!.text.primary,
      subscribeText3: brandColors!.text.secondary,
      subscribeText4: brandColors!.text.secondary,
      subscribeCardBg: 'linear-gradient(90deg, #FFFFFF 0%, #FDEAF2 100%)',
      cycleTextGradient: 'linear-gradient(180deg, #FF7FB5 0%, #EE3BA4 100%)',
      badgeGradient: 'linear-gradient(132deg, #FF7FB5 0%, #EE3BA4 100%)',
      pageBg: '#fdf2f8',
      paymentCardBg: '#fff',
      paymentCardBorder: '#e5e7eb',
      paymentCardActiveBorder: '#ec3a9c',
      tagGradient: 'linear-gradient(90deg, #FF4754 0%, #FEA94C 100%)',
      titleColor: '#1f2937',
      schemes: {
        discounted: {
          bg: 'linear-gradient(270deg, #FFE1E4 0%, #FFD9DF 100%)',
          priceBg: 'linear-gradient(90deg, #FF7FED 0%, #FF516B 100%)',
          titleColor: '#77382C',
          descColor: '#A96D61',
          borderColor: 'transparent',
          unitPriceColor: '#A96D61',
          originalPriceColor: '#A96D61',
          cycleLabelBg: 'linear-gradient(90deg, #FFC2CB 0%, #FECCD3 100%)',
          cycleLabelColor: '#A96D61',
        },
        normal: {
          bg: 'linear-gradient(90deg, #E9DBFF 0%, #EEE3FF 100%)',
          priceBg: 'linear-gradient(90deg, #C77CFF 0%, #6773F3 100%)',
          titleColor: '#3D426E',
          descColor: '#797CA0',
          borderColor: 'transparent',
          unitPriceColor: '#797CA0',
          originalPriceColor: '#797CA0',
          cycleLabelBg: 'linear-gradient(270deg, #E9DBFF 0%, #DEE2FE 100%)',
          cycleLabelColor: '#3D426E',
        },
        fakeDiscount: {
          bg: 'linear-gradient(270deg, #FFECDE 0%, #FFDFB8 100%)',
          priceBg: 'linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%)',
          titleColor: '#77382C',
          descColor: '#9E7739',
          borderColor: 'transparent',
          unitPriceColor: '#9E7739',
          originalPriceColor: '#9E7739',
          cycleLabelBg: 'linear-gradient(270deg, #FFD1AE 0%, #FFD5A2 100%)',
          cycleLabelColor: '#775217',
        },
      },
    },
  },

  chapter: {
    texts: chapterCommonTexts,
    darkPaymentSelector: false,
    themeDefaults: {
      background: '#F6F7F9',
      size: '38',
    },
    colors: {
      coinsGradient: `linear-gradient(180deg, ${brandColors!.primary}, ${brandColors!.bg.global})`,
      coinsPopupText1: brandColors!.text.inverse,
      coinsPopupText2: primaryGradientStart,
      coinsPopupText3: brandColors!.text.secondary,
      coinsPopupText4: brandColors!.text.inverse,
      vipBg: brandColors!.bg.card,
      subscribeActiveBg: brandColors!.bg.card,
      purpleTextGradient: 'linear-gradient(180deg, #F162B6 0%, #FF99C4 100%)',
      pinkGradient: primaryGradient,
      subscribeCardBg: 'linear-gradient(90deg, #FFFFFF 0%, #FDEAF2 100%)',
      purchaseActiveBg: '#FFF1F7',
      memberSubscribeBg: '/images/storlyn/chapter/member_subscribe_bg.webp',
      showVipIcon: true,
    },
  },
}
